/**
 * The pixel-size check for a platen variant slot (Phase 86, D-13).
 *
 * Before a PNG dropped on a platen variant slot is hashed or uploaded, its
 * pixel size is read from the header (`readPngDimensions`, 24 bytes, never
 * decoded). A file matches only when it is exactly the platen at 300 DPI
 * (`PLATEN_DIMS`: 10x12 = 3000 x 3600, 14x16 = 4200 x 4800, 16x18 = 4800 x
 * 5400). A mismatch opens the shared warn-not-block dialog and the upload
 * starts only on "OK, I understand"; "I'll fix it" (or Escape) discards the
 * file. A file whose size cannot be read resolves `unreadable` with no dialog,
 * so the cell shows its inline error instead.
 *
 * A slot may pass a `notice` (already translated) that the operator must see
 * before this upload, for example that it replaces the file a store SKU prints
 * today (Phase 88, D-12). It shares the ONE dialog, so one drop never opens
 * two dialogs back to back: with a pixel mismatch the notice sits above the
 * pixel message and the one "OK, I understand" answers both; with a matching
 * PNG the dialog shows the notice alone, with Upload and Cancel. An unreadable
 * file still resolves `unreadable` with no dialog (nothing uploads).
 *
 * Round 4 (R4-5): pass the org's Platen variants (`pixels`, from the platen
 * context read) and a file must be that platen's Large px instead (a Small
 * file is the Large canvas too). With no argument, or null, it is exactly
 * today's `PLATEN_DIMS` check.
 *
 * One dialog at a time: a second check while one is open answers the first as
 * `cancel`. Mount this ONCE per surface (the Designs tier, the Product updates
 * focused view) and pass `guard` to every slot, so there is only ever one
 * dialog. Slots on products without rules never call this.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  PLATEN_DIMS,
  platenFilePixels,
  type DtgPlatenCode,
  type EffectivePlatenPixels,
} from '@threadheads/shared';
import { WarnNotBlockDialog } from '@/components/WarnNotBlockDialog';
import { readPngDimensions } from '@/lib/png-dimensions';

export type PlatenPixelCheckResult = 'ok' | 'proceed' | 'cancel' | 'unreadable';

/** A message the operator must see before this upload, already translated. */
export interface PlatenUploadNotice {
  title: string;
  body: string;
}

/**
 * The check itself, as a surface passes it down to its slots. With a
 * `notice`, the answer is never `ok`: the operator always answers the dialog
 * (`proceed` or `cancel`), unless the file is `unreadable`.
 */
export type PlatenPixelGuard = (
  file: File,
  platen: DtgPlatenCode,
  notice?: PlatenUploadNotice,
) => Promise<PlatenPixelCheckResult>;

interface PendingCheck {
  fileName: string;
  platen: DtgPlatenCode;
  /** The pixel sizes, or null when the PNG is the platen size (a notice alone). */
  pixels: { width: number; height: number; expectedWidth: number; expectedHeight: number } | null;
  notice: PlatenUploadNotice | null;
  resolve: (answer: PlatenPixelCheckResult) => void;
}

/** The px a platen's file must be: the org's Large numbers, or today's table without them. */
export function expectedPlatenFilePixels(
  pixels: EffectivePlatenPixels | null | undefined,
  platen: DtgPlatenCode,
): { width: number; height: number } | null {
  if (pixels?.[platen]) return platenFilePixels(pixels, platen);
  const dims = PLATEN_DIMS[platen];
  return dims ? { width: dims.w, height: dims.h } : null;
}

export function usePlatenPixelCheck(orgPixels?: EffectivePlatenPixels | null) {
  const { t } = useTranslation('productSync');
  // Read at check time, so `guard` keeps one identity across renders.
  const pixelsRef = useRef(orgPixels);
  pixelsRef.current = orgPixels;
  const [pending, setPending] = useState<PendingCheck | null>(null);
  const pendingRef = useRef<PendingCheck | null>(null);
  // The last check shown, so the dialog keeps its text while it animates out.
  const [shown, setShown] = useState<PendingCheck | null>(null);

  const settle = useCallback((answer: PlatenPixelCheckResult) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(answer);
  }, []);

  useEffect(() => () => settle('cancel'), [settle]);

  const guard = useCallback<PlatenPixelGuard>(async (file, platen, notice) => {
    const dims = await readPngDimensions(file);
    if (!dims) return 'unreadable';
    const expected = expectedPlatenFilePixels(pixelsRef.current, platen);
    const pixelsMatch = !expected || (dims.width === expected.width && dims.height === expected.height);
    if (pixelsMatch && !notice) return 'ok';

    return new Promise<PlatenPixelCheckResult>((resolve) => {
      // Only one dialog at a time: an older open check is answered "cancel".
      pendingRef.current?.resolve('cancel');
      const next: PendingCheck = {
        fileName: file.name,
        platen,
        pixels:
          pixelsMatch || !expected
            ? null
            : { width: dims.width, height: dims.height, expectedWidth: expected.width, expectedHeight: expected.height },
        notice: notice ? { title: notice.title, body: notice.body } : null,
        resolve,
      };
      pendingRef.current = next;
      setPending(next);
      setShown(next);
    });
  }, []);

  const view = pending ?? shown;
  const pixels = view?.pixels ?? null;
  const notice = view?.notice ?? null;
  // A notice alone is a plain "go ahead or not": its own title, Upload and
  // Cancel. With a pixel mismatch the pixel title leads, the notice sits
  // above the pixel message, and the warn-not-block labels answer both.
  const noticeOnly = notice !== null && pixels === null;
  const dialog = (
    <WarnNotBlockDialog
      open={pending !== null}
      contentClassName="max-w-md"
      title={view ? (noticeOnly ? notice.title : t('platen.pixelDialog.title', { platen: view.platen })) : ''}
      description={
        view ? (
          <>
            {notice &&
              (noticeOnly ? (
                <p>{notice.body}</p>
              ) : (
                <div className="space-y-1 rounded-md border bg-muted/40 px-3 py-2">
                  <p className="font-medium text-foreground">{notice.title}</p>
                  <p>{notice.body}</p>
                </div>
              ))}
            {pixels && (
              <p className="tabular-nums">
                <Trans
                  t={t}
                  i18nKey="platen.pixelDialog.description"
                  values={{
                    fileName: view.fileName,
                    width: pixels.width,
                    height: pixels.height,
                    platen: view.platen,
                    expectedWidth: pixels.expectedWidth,
                    expectedHeight: pixels.expectedHeight,
                  }}
                  components={[<span key="file" className="break-all font-mono text-foreground" />]}
                />
              </p>
            )}
          </>
        ) : null
      }
      {...(noticeOnly ? { proceedLabel: t('common:upload'), fixLabel: t('common:cancel') } : {})}
      onProceed={() => settle('proceed')}
      onFix={() => settle('cancel')}
    />
  );

  return { guard, dialog };
}
